import { z } from 'zod';
import { OFFSITE_KANAELE, type OffsiteKanal } from './kampagnen';

/**
 * Retail Media RM-9: Offsite / Audience Extension. Je aktivem Produkt einer Offsite-Kampagne und je Kanal ein
 * Tracking-Link https://<PUBLIC_URL>/v1/rm/r/<code>. Der Link leitet auf die Produktseite im Shop weiter und rechnet
 * den Klick mit dem Gebot der Anzeigengruppe ab (CPC, kein Zweitpreis – offsite gibt es keine Auktion).
 *
 * Werbesitzung: Die Weiterleitung hängt RM_OFFSITE_PARAMETER=<Sitzung> an die Zieladresse. Das Shop-SDK übernimmt
 * diesen Wert als Sitzung (localStorage und Warenkorb-Attribut _dxl_rm_sitzung) – so wird eine spätere Bestellung
 * dem Offsite-Klick zugeordnet.
 */

export const OFFSITE_KANAL_LISTE = ['meta', 'google', 'tiktok', 'newsletter', 'sonstige'] as const satisfies readonly OffsiteKanal[];

/** Parameter in der Zieladresse: Werbesitzung (24 Hex-Zeichen wie die Sitzung des SDK) */
export const RM_OFFSITE_PARAMETER = 'dxl_rm_sitzung';

export const OFFSITE_GRENZEN = {
  /** höchstens ein berechneter Klick je (IP-Hash, Produkt, Kanal) in diesem Zeitraum */
  sperreMinuten: 30,
  /** Links je Seite der Liste */
  linksJeSeite: 200,
} as const;

/** Hinweis je Kanal: wo der Feed hingehört */
export const OFFSITE_KANAL_HINWEIS: Record<OffsiteKanal, string> = {
  meta: 'Im Commerce Manager als Datenfeed (CSV) hochladen – Spalte „link“ ist der Tracking-Link.',
  google: 'Im Merchant Center als zusätzliche Feed-Quelle (Datei) hochladen – Spalte „link“ ist der Tracking-Link.',
  tiktok: 'Im TikTok-Katalog als Datenfeed hochladen – Spalte „link“ ist der Tracking-Link.',
  newsletter: 'Tracking-Links direkt in den Newsletter übernehmen.',
  sonstige: 'Tracking-Links für weitere Kanäle (Affiliate, Social Posts, QR-Codes).',
};

const Kanal = z.enum(OFFSITE_KANAL_LISTE, { errorMap: () => ({ message: 'Bitte einen Kanal wählen' }) });

export const OffsiteLinksAbfrage = z.object({
  kanal: Kanal,
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(OFFSITE_GRENZEN.linksJeSeite).default(50),
});
export const OffsiteExportAbfrage = z.object({ kanal: Kanal });

/* ------------------------------------------------------------------ Antworten */

export interface OffsiteLink {
  id: string;
  code: string;
  /** Tracking-Link */
  url: string;
  kanal: OffsiteKanal;
  gruppeId: string;
  gruppe: string;
  produktId: string;
  titel: string;
  bild: string | null;
  /** niedrigster Preis der Varianten */
  preisCent: number | null;
  /** Produktseite im Shop – null: kein Shop verbunden oder Produkt ohne Handle */
  zielUrl: string | null;
  /** Produkt steht im Shop und die Anzeige ist aktiv – sonst nicht im Feed und ohne Abrechnung */
  aktiv: boolean;
}

export interface OffsiteLinkSeite {
  eintraege: OffsiteLink[];
  weiter: string | null;
  /** bei diesem Aufruf neu angelegte Links (Produkte, die seit dem letzten Aufruf dazukamen) */
  neu: number;
}

export interface OffsiteKanalStand {
  kanal: OffsiteKanal;
  links: number;
  /** letzte 30 Tage */
  klicks: number;
  berechnet: number;
  kostenCent: number;
  bestellungen: number;
  umsatzCent: number;
  /** Aufrufe von Bots und Linkprüfern – nie berechnet */
  bots: number;
}

export interface OffsiteUebersicht {
  kampagneId: string;
  /** aktive Produkte in den Anzeigengruppen der Kampagne */
  produkte: number;
  /** Shop-Adresse für die Zieladressen – null: kein Marktplatz-Shop verbunden */
  shop: string | null;
  kanaele: OffsiteKanalStand[];
}

export const offsiteKanalName = (k: OffsiteKanal) => OFFSITE_KANAELE[k];
